import { noop } from 'lodash/fp';
import Keycloak from 'keycloak-js';
import type { Store } from 'app/store/initializer';
import { doInitiateApplication } from 'app/store/startup/actions';
import { KEYCLOAK_CONNECTION_ERROR } from 'app/store/users/types.js';

const KEYCLOAK_CONFIG_URL = '/config/keycloak';
const KEYCLOAK_ERR_NOT_AUTHED = 'not authenticated despite login prompt';
const KEYCLOAK_ERR_LOAD_USER_INFO_FAILED = 'failure at getting user information via loadUserInfo()';

// defined in keycloak admin, amount of idle time before a session expires
const KEYCLOAK_SSO_SESSION_IDLE = 30 * 60 * 1000;
// buffer time after a session expires during which it hasn't actually expired yet
const KEYCLOAK_IDLE_BUFFER = 2 * 60 * 1000;

const handleKeycloakError = (msg: string) => {
    throw new Error(`keycloak: ${msg}`);
};

const KEYCLOAK_TOKEN_MIN_VALIDITY_SECONDS = 10;

let autoRefreshTimeoutId: null | NodeJS.Timeout = null;

export function authLogout() {
    window.europa?.keycloak?.logout({ redirectUri: window.location.origin });
}

export function getJwtToken() {
    return window.europa?.keycloak?.token;
}

export function refreshJwtToken({ onSuccess, onError } = { onSuccess: noop, onError: noop }) {
    const keycloak = window.europa?.keycloak;

    if (!keycloak) {
        onSuccess(null);
        return;
    }

    if (autoRefreshTimeoutId) {
        clearTimeout(autoRefreshTimeoutId);
        autoRefreshTimeoutId = null;
    }

    keycloak
        .updateToken(KEYCLOAK_TOKEN_MIN_VALIDITY_SECONDS)
        .then((refreshed) => {
            onSuccess(refreshed ? keycloak.token : null);

            if (!autoRefreshTimeoutId) {
                // set a timer to attempt a token refresh after the token will expire from inactivity
                // if it has expired the browser gets redirected to the login screen
                // we refresh the token before every api call which will reset the timer
                autoRefreshTimeoutId = setTimeout(
                    refreshJwtToken,
                    KEYCLOAK_SSO_SESSION_IDLE + KEYCLOAK_IDLE_BUFFER
                );
            }
        })
        .catch((e: Error) => {
            console.error('Failed to update Keycloak token', e);
            onError();
        });
}

export function initializeKeycloak(store: Store) {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('accessToken') ?? undefined;
    const keycloak = new Keycloak(KEYCLOAK_CONFIG_URL);

    const handleKeycloakUserInfo = ({
        preferred_username: username
    }: {
        preferred_username?: string;
    }) => {
        window.europa ??= {};
        window.europa.keycloak = keycloak;

        store.dispatch(doInitiateApplication({ username, jwt: keycloak.token }));
    };

    return keycloak
        .init({
            onLoad: 'login-required',
            checkLoginIframe: false,
            token: accessToken
        })
        .then((authenticated) => {
            if (authenticated) {
                return keycloak
                    .loadUserInfo()
                    .then(handleKeycloakUserInfo)
                    .catch(() => handleKeycloakError(KEYCLOAK_ERR_LOAD_USER_INFO_FAILED));
            }
            handleKeycloakError(KEYCLOAK_ERR_NOT_AUTHED);
        })
        .catch((e) => {
            console.error('Error initializing keycloak client', e);
            store.dispatch({ type: KEYCLOAK_CONNECTION_ERROR });
        });
}
